import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import {
  AudioLines,
  CheckCircle2,
  Clapperboard,
  Film,
  History,
  Loader2,
  Plus,
  RotateCcw,
  Smartphone,
  Upload,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Progress } from '@/components/ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  FlowChatLayout,
  AssistantBubble,
  UserBubble,
  ChatInputArea,
  ChatSection,
  RightPanelHeading,
} from '@/components/FlowChatLayout';
import { ReelsShortsHistoryPanel } from '@/components/reelsShorts/ReelsShortsHistoryPanel';
import { ReelsShortsPipeline } from '@/components/reelsShorts/ReelsShortsPipeline';
import { ReelsShortsCompositionStages } from '@/components/reelsShorts/ReelsShortsCompositionStages';
import { ReelsShortsTranscriptPanel } from '@/components/reelsShorts/ReelsShortsTranscriptPanel';
import { ReelsShortsEditorCutPanel } from '@/components/reelsShorts/ReelsShortsEditorCutPanel';
import { ReelsShortsFinalVideoPanel } from '@/components/reelsShorts/ReelsShortsFinalVideoPanel';
import { ReelsShortsLayoutEngine } from '@/components/reelsShorts/ReelsShortsLayoutEngine';
import { ReelsShortsBrollCatalog, ReelsShortsBrollLookPicker } from '@/components/reelsShorts/ReelsShortsBrollPreview';
import { ReelsShortsTitleCatalog } from '@/components/reelsShorts/ReelsShortsTitleTemplates';
import api from '@/lib/api';
import { formatApiError } from '@/lib/formatApiError';
import { getIntakeNavPrefill } from '@/lib/intakeNavPrefill';
import { readAgentHandoff } from '@/lib/agentHandoff';
import { AgentHandoffGate } from '@/components/createPlan/AgentHandoffGate';
import { projectPipelineStep, DEFAULT_BROLL_VISUAL_STYLE } from '@/lib/reelsShortsBroll';
import { cleanAllToast } from '@/lib/reelsShortsAudioClean';
import { reelsShortsEditorPath } from '@/lib/reelsShortsEditor';
import {
  readFlowHistoryPanelOpen,
  writeFlowHistoryPanelOpen,
} from '@/components/creationFlows/FlowHistoryPanel';

import { TwinneeLogo } from '@/components/TwinneeLogo';
const HISTORY_PANEL_KEY = 'reels_shorts';
const VIDEO_ACCEPT = 'video/mp4,video/quicktime,video/webm,video/x-m4v,.mp4,.mov,.mkv,.webm,.m4v,.avi';
const TRANSCRIPT_LANGUAGES = [
  { code: 'auto', label: 'Auto (as spoken)' },
  { code: 'en', label: 'English' },
  { code: 'hi', label: 'Hindi' },
  { code: 'ur', label: 'Urdu' },
  { code: 'es', label: 'Spanish' },
  { code: 'fr', label: 'French' },
  { code: 'de', label: 'German' },
  { code: 'pt', label: 'Portuguese' },
  { code: 'ar', label: 'Arabic' },
  { code: 'ja', label: 'Japanese' },
  { code: 'ko', label: 'Korean' },
  { code: 'zh', label: 'Chinese' },
  { code: 'bn', label: 'Bengali' },
  { code: 'mr', label: 'Marathi' },
  { code: 'ta', label: 'Tamil' },
  { code: 'te', label: 'Telugu' },
  { code: 'kn', label: 'Kannada' },
];

function languageLabel(code) {
  const found = TRANSCRIPT_LANGUAGES.find((l) => l.code === code);
  return found?.label || code || 'Auto';
}

function isAbortError(err) {
  return err?.code === 'ERR_CANCELED' || err?.name === 'CanceledError' || err?.name === 'AbortError';
}

function historyRowPayload(row) {
  if (!row?.id) return null;
  return {
    project: {
      id: row.id,
      title: row.title || 'Untitled reel',
      status: row.status || 'draft',
      language: row.language || 'auto',
      snippets: [],
      has_final_video: Boolean(row.has_final_video),
      pipeline_path: row.pipeline_path || `/reels-shorts/create?project=${row.id}`,
    },
    job: null,
    render_job: null,
  };
}

async function uploadSnippetToS3(file) {
  const filename = file.name || `reel-snippet-${Date.now()}.mp4`;
  const contentType = file.type || 'video/mp4';
  const { data: presigned } = await api.post('/media/presigned-url', null, {
    params: { filename, content_type: contentType },
  });
  const uploadUrl = presigned.upload_url || presigned.presigned_url;
  const s3Key = presigned.s3_key;
  const viewUrl = presigned.view_url || presigned.object_url || presigned.s3_url;
  if (!uploadUrl || !s3Key) throw new Error('Could not get upload URL');
  const putRes = await fetch(uploadUrl, {
    method: 'PUT',
    body: file,
    headers: { 'Content-Type': contentType },
  });
  if (!putRes.ok) throw new Error(`S3 upload failed (${putRes.status})`);
  return { s3_key: s3Key, s3_url: viewUrl, filename, content_type: contentType };
}

/* ── Chat step IDs ───────────────────────────────────────────────────────── */
const STEP = {
  FILES: 'files',
  TITLE: 'title',
  LANGUAGE: 'language',
  BROLL: 'broll',
  CONFIRM: 'confirm',
};

export function ReelsShortsFlowPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const fileRef = useRef(null);
  const chatEndRef = useRef(null);

  /* ── form state ── */
  const [title, setTitle] = useState('');
  const [files, setFiles] = useState([]);
  const [language, setLanguage] = useState('auto');
  const [brollVisualStyle, setBrollVisualStyle] = useState(DEFAULT_BROLL_VISUAL_STYLE);
  const [editingMode, setEditingMode] = useState('auto');
  const [cleanupLevel, setCleanupLevel] = useState('balanced');

  /* ── chat step progression ── */
  const [chatStep, setChatStep] = useState(STEP.FILES);
  const [titleDraft, setTitleDraft] = useState('');

  /* ── project / job state ── */
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState('');
  const [project, setProject] = useState(null);
  const [job, setJob] = useState(null);
  const [renderJob, setRenderJob] = useState(null);
  const [cutting, setCutting] = useState(false);
  const [recutting, setRecutting] = useState(false);
  const [restoringVersionId, setRestoringVersionId] = useState(null);
  const [rendering, setRendering] = useState(false);
  const [includeBroll, setIncludeBroll] = useState(true);
  const [includeSubtitles, setIncludeSubtitles] = useState(true);
  const [changingLanguage, setChangingLanguage] = useState(false);
  const [cleaningAll, setCleaningAll] = useState(false);

  /* ── history panel ── */
  const [historyOpen, setHistoryOpen] = useState(() => readFlowHistoryPanelOpen(HISTORY_PANEL_KEY, false));
  const [historyRefreshKey, setHistoryRefreshKey] = useState(0);
  const [loadingHistoryId, setLoadingHistoryId] = useState(null);
  const projectCacheRef = useRef(new Map());

  const queryProjectId = searchParams.get('project');
  const agentHandoff = useMemo(
    () => (queryProjectId ? null : readAgentHandoff(location.state)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [location.state],
  );
  const [handoffReady, setHandoffReady] = useState(false);

  const projectId = project?.id || null;
  const pipelineRunning = ['pending', 'running'].includes(String(job?.status || '').toLowerCase());
  const renderRunning = ['pending', 'running'].includes(String(renderJob?.status || '').toLowerCase());
  const jobRunning = pipelineRunning || renderRunning;
  const displayJob = renderRunning || (renderJob && !pipelineRunning) ? renderJob : job;
  const showSetupForm = !projectId;
  const languageBusy = changingLanguage;
  const canRunEditor = Boolean(projectId)
    && !changingLanguage
    && (project?.snippets || []).some(
      (s) => String(s.status || '').startsWith('ready') || (s.transcript || '').trim(),
    );

  /* ── init from location state ── */
  useEffect(() => {
    const nav = getIntakeNavPrefill(location.state);
    if (nav?.title) setTitle(nav.title);
    if (nav?.language) {
      const code = String(nav.language).trim().toLowerCase();
      const match = TRANSCRIPT_LANGUAGES.find(
        (l) => l.code === code || l.label.toLowerCase() === code,
      );
      setLanguage(match?.code || 'auto');
    }
    if (readAgentHandoff(location.state)) setHandoffReady(true);
  }, [location.state]);

  /* ── scroll chat to bottom on new step ── */
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatStep, projectId]);

  /* ── history handlers ── */
  const toggleHistory = useCallback(() => {
    setHistoryOpen((prev) => {
      const next = !prev;
      writeFlowHistoryPanelOpen(HISTORY_PANEL_KEY, next);
      return next;
    });
  }, []);

  const resetForNewProject = useCallback(() => {
    setTitle('');
    setTitleDraft('');
    setFiles([]);
    setLanguage('auto');
    setProject(null);
    setJob(null);
    setRenderJob(null);
    setCutting(false);
    setRendering(false);
    setIncludeBroll(true);
    setIncludeSubtitles(true);
    setChangingLanguage(false);
    setUploadProgress('');
    setChatStep(STEP.FILES);
    if (fileRef.current) fileRef.current.value = '';
    setSearchParams({}, { replace: true });
  }, [setSearchParams]);

  const applyLoadedProject = useCallback((data, { persistCache = true } = {}) => {
    if (!data?.project) return;
    const nextRender = data.render_job || null;
    const renderSuperseded = Boolean(nextRender?.superseded)
      || /superseded by re-cut/i.test(String(nextRender?.error_message || nextRender?.message || ''));
    if (persistCache) {
      projectCacheRef.current.set(data.project.id, {
        project: data.project,
        job: data.job || null,
        render_job: renderSuperseded ? null : nextRender,
      });
    }
    setProject(data.project);
    setJob(data.job || null);
    if (renderSuperseded) {
      setRenderJob((prev) => {
        const prevStatus = String(prev?.status || '').toLowerCase();
        if (['pending', 'running'].includes(prevStatus)) return prev;
        return null;
      });
    } else {
      setRenderJob(nextRender);
    }
    setTitle(data.project?.title || '');
    setLanguage(data.project?.language || 'auto');
    setChangingLanguage(false);
    setChatStep(STEP.CONFIRM); // jump to processing view for loaded projects
  }, []);

  const loadProject = useCallback(async (id, { signal } = {}) => {
    if (!id) return;
    const { data } = await api.get(`/reels-shorts/projects/${id}`, { signal, timeout: 45_000 });
    applyLoadedProject(data);
  }, [applyLoadedProject]);

  const selectHistoryProject = useCallback((row) => {
    if (!row?.id) return;
    if (row.id === queryProjectId && row.id === projectId) return;
    const cached = projectCacheRef.current.get(row.id);
    applyLoadedProject(cached || historyRowPayload(row), { persistCache: Boolean(cached) });
    if (!cached) setLoadingHistoryId(row.id);
    setSearchParams({ project: row.id }, { replace: true });
  }, [applyLoadedProject, projectId, queryProjectId, setSearchParams]);

  useEffect(() => {
    if (!queryProjectId) { setLoadingHistoryId(null); return undefined; }
    const cached = projectCacheRef.current.get(queryProjectId);
    if (cached) applyLoadedProject(cached);
    else setLoadingHistoryId(queryProjectId);
    const controller = new AbortController();
    loadProject(queryProjectId, { signal: controller.signal })
      .catch((err) => { if (!isAbortError(err)) toast.error(formatApiError(err, 'Could not load project')); })
      .finally(() => { if (!controller.signal.aborted) setLoadingHistoryId(null); });
    return () => controller.abort();
  }, [applyLoadedProject, loadProject, queryProjectId]);

  /* ── polling ── */
  useEffect(() => {
    if (!projectId || !jobRunning) return undefined;
    let cancelled = false;
    const controller = new AbortController();
    const tick = async () => {
      try {
        const { data } = await api.get(`/reels-shorts/projects/${projectId}`, { signal: controller.signal, timeout: 45_000 });
        if (cancelled) return;
        const prevStatus = job?.status;
        const prevRenderStatus = renderJob?.status;
        applyLoadedProject(data);
        const nextPipe = data.job?.status;
        const nextRender = data.render_job?.status;
        if (nextPipe && nextPipe !== prevStatus) {
          if (nextPipe === 'awaiting_review') {
            setHistoryRefreshKey((k) => k + 1);
            setChangingLanguage(false);
            const hasSpeakers = (data.project?.snippets || []).some(
              (s) => Array.isArray(s.speaker_audio_tracks) && s.speaker_audio_tracks.length,
            );
            toast.success(
              hasSpeakers
                ? 'Transcripts ready — combined mix + per-speaker audio under Mapped transcriptions'
                : 'Transcripts ready — edit them, then run the AI editor',
            );
          }
          if (nextPipe === 'completed') { setHistoryRefreshKey((k) => k + 1); setCutting(false); setChangingLanguage(false); toast.success('Editor cut ready'); }
          if (nextPipe === 'failed') { setChangingLanguage(false); toast.error(data.job?.error_message || 'REEL&SHORTS run failed'); }
        }
        if (nextRender && nextRender !== prevRenderStatus) {
          if (nextRender === 'completed') { setHistoryRefreshKey((k) => k + 1); setRendering(false); toast.success('Final video ready'); }
          if (nextRender === 'failed' || nextRender === 'cancelled') {
            setRendering(false);
            const err = String(data.render_job?.error_message || data.render_job?.message || '');
            const superseded = data.render_job?.superseded
              || /superseded by re-cut/i.test(err);
            if (!superseded) {
              toast.error(err || 'Final video failed');
            }
          }
        }
      } catch { /* ignore poll errors */ }
    };
    const id = setInterval(tick, 2500);
    tick();
    return () => { cancelled = true; controller.abort(); clearInterval(id); };
  }, [applyLoadedProject, projectId, jobRunning, job?.status, renderJob?.status]);

  /* ── actions ── */
  const onPickFiles = (event) => {
    const list = Array.from(event.target.files || []);
    setFiles(list);
  };

  const advanceToTitle = () => {
    if (!files.length) { toast.error('Pick at least one video clip'); return; }
    setChatStep(STEP.TITLE);
  };

  const advanceToLanguage = () => {
    const trimmed = titleDraft.trim();
    if (!trimmed) { toast.error('Enter a title'); return; }
    setTitle(trimmed);
    setChatStep(STEP.LANGUAGE);
  };

  const advanceToBroll = () => setChatStep(STEP.BROLL);
  const advanceToConfirm = () => setChatStep(STEP.CONFIRM);

  const createProject = async () => {
    setLoading(true);
    try {
      const uploaded = [];
      for (let i = 0; i < files.length; i += 1) {
        setUploadProgress(`Uploading ${i + 1}/${files.length}…`);
        // eslint-disable-next-line no-await-in-loop
        uploaded.push(await uploadSnippetToS3(files[i]));
      }
      setUploadProgress('Saving project…');
      const { data } = await api.post('/reels-shorts/projects', {
        title: title.trim(),
        language: language || 'auto',
        aspect_ratio: '9:16',
        broll_visual_style: brollVisualStyle,
        editing_mode: editingMode || 'auto',
        cleanup_level: cleanupLevel || 'balanced',
        snippets: uploaded,
      });
      applyLoadedProject({
        project: data.project,
        job: { id: data.job_id, status: data.status || 'pending', progress: 0, message: 'Queued' },
        render_job: null,
      });
      setSearchParams({ project: data.project.id }, { replace: true });
      setHistoryRefreshKey((k) => k + 1);
      toast.success('Snippets uploaded — splitting audio & video');
    } catch (err) {
      toast.error(formatApiError(err, 'Could not create reel'));
    } finally {
      setLoading(false);
      setUploadProgress('');
    }
  };

  const runEditor = async () => {
    if (!projectId) return;
    setCutting(true);
    try {
      const { data } = await api.post(`/reels-shorts/projects/${projectId}/editor-cut`);
      setJob({ id: data.job_id, status: data.status || 'running', progress: 86, message: 'ChatGPT editor is ordering snippets and suggesting B-ROLL' });
      toast.success('AI editor started — transcripts stay verbatim');
    } catch (err) { setCutting(false); toast.error(formatApiError(err, 'Could not run AI editor')); }
  };

  const recutPolishedReel = async () => {
    if (!projectId || recutting || cutting || rendering || renderRunning) return;
    setRecutting(true);
    setCutting(true);
    // Hide prior failed encode banner while the re-cut owns the project.
    setRenderJob(null);
    setRendering(false);
    try {
      const { data } = await api.post(`/reels-shorts/projects/${projectId}/recut`, {
        auto_render: true,
        include_broll: includeBroll,
        include_subtitles: includeSubtitles,
        generate_all_formats: false,
      });
      setJob({
        id: data.job_id,
        status: data.status || 'running',
        progress: 86,
        message: data.message || 'Re-cutting polished reel on same clips',
      });
      if (Array.isArray(data.render_versions) && project) {
        setProject({ ...project, render_versions: data.render_versions, final_video_url: project.final_video_url });
      }
      setHistoryRefreshKey((k) => k + 1);
      toast.success(
        data.archived_export
          ? 'Re-cutting same clips — prior export saved in history'
          : 'Re-cutting same clips into a polished vertical reel',
      );
    } catch (err) {
      toast.error(formatApiError(err, 'Could not re-cut reel'));
      setCutting(false);
    } finally {
      setRecutting(false);
    }
  };

  const restoreRenderVersion = async (version) => {
    if (!projectId || !version?.id || restoringVersionId) return;
    setRestoringVersionId(version.id);
    try {
      const { data } = await api.post(
        `/reels-shorts/projects/${projectId}/render-versions/${version.id}/restore`,
      );
      if (data?.project) setProject(data.project);
      toast.success('Restored previous export version');
    } catch (err) {
      toast.error(formatApiError(err, 'Could not restore export version'));
    } finally {
      setRestoringVersionId(null);
    }
  };

  const renderFinal = async ({ generateAllFormats = false, formats = null } = {}) => {
    if (!projectId || rendering) return;
    setRendering(true);
    try {
      const selected = Array.isArray(formats)
        ? formats.filter(Boolean)
        : null;
      const { data } = await api.post(`/reels-shorts/projects/${projectId}/render`, {
        include_broll: includeBroll,
        include_subtitles: includeSubtitles,
        generate_all_formats: Boolean(generateAllFormats) || Boolean(selected && selected.length > 1),
        formats: selected && selected.length ? selected : undefined,
      });
      setRenderJob({
        id: data.job_id,
        job_type: 'reels_shorts_render',
        status: data.status || 'pending',
        progress: Number(data.progress || 0),
        video_percent: Number(data.progress || 0),
        message: generateAllFormats || (selected && selected.length > 1)
          ? `Queued — generating ${(selected || []).length || 'all'} export format(s)`
          : 'Queued — rendering final video',
      });
      setHistoryRefreshKey((k) => k + 1);
      toast.success(
        data.reused
          ? 'Final video already queued'
          : generateAllFormats || (selected && selected.length > 1)
            ? 'Selected formats queued — we will email links when ready'
            : 'Final video queued — we will email the link when ready',
      );
    } catch (err) {
      toast.error(formatApiError(err, 'Could not start final video'));
    } finally {
      setRendering(false);
    }
  };

  const cleanAllAudio = async () => {
    if (!projectId || cleaningAll) return;
    setCleaningAll(true);
    try {
      const { data } = await api.post(`/reels-shorts/projects/${projectId}/clean-audio`, { preset: 'auto' }, { timeout: 600_000 });
      if (data?.project) setProject(data.project);
      const failed = (data?.project?.snippets || []).filter((s) => String(s.error_message || '').toLowerCase().includes('clean'));
      if (failed.length) toast.error(`Cleaned most clips; ${failed.length} still need attention`);
      else toast.success(cleanAllToast(data?.project?.snippets || []));
    } catch (err) { toast.error(formatApiError(err, 'Could not clean snippet audio')); }
    finally { setCleaningAll(false); }
  };

  const retry = async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      const { data } = await api.post(`/reels-shorts/projects/${projectId}/retry`);
      setJob({ id: data.job_id, status: 'pending', progress: 0, message: 'Retry queued — splitting audio & video' });
      toast.info('Re-running Split A/V + transcribe (rebuilds speaker audio tracks)');
    } catch (err) { toast.error(formatApiError(err, 'Retry Split A/V failed')); }
    finally { setLoading(false); }
  };

  const changeTranscriptLanguage = async (code, { retranscribe = true } = {}) => {
    const next = String(code || 'auto').trim().toLowerCase() || 'auto';
    const current = String(project?.language || language || 'auto').trim().toLowerCase();
    if (!projectId) { setLanguage(next); return; }
    if (changingLanguage) return;
    if (next === current && !retranscribe) return;
    setLanguage(next);
    setChangingLanguage(true);
    try {
      const { data } = await api.patch(`/reels-shorts/projects/${projectId}/language`, { language: next, retranscribe: true });
      if (data?.project) setProject(data.project);
      setJob({ id: data.job_id, status: data.status || 'running', progress: 8, message: `Re-transcribing (${languageLabel(next)})` });
      toast.success(`Re-transcribing as spoken — ${languageLabel(next)}`);
    } catch (err) { setLanguage(current); toast.error(formatApiError(err, 'Could not change language')); }
    finally { setChangingLanguage(false); }
  };

  const activeStep = useMemo(() => projectPipelineStep(project, displayJob), [project, displayJob]);
  const progress = Math.max(0, Math.min(100, Number(displayJob?.video_percent != null ? displayJob.video_percent : displayJob?.progress || 0)));
  const isRenderProgress = String(displayJob?.job_type || '') === 'reels_shorts_render' || renderRunning;

  /* ── Left panel: chat thread ─────────────────────────────────────────── */
  const leftContent = (
    <>
      <AgentHandoffGate
        handoff={agentHandoff}
        ready={handoffReady}
        flowLabel="REEL&SHORTS"
        busy={loading}
        onStart={createProject}
        fields={[
          {
            id: 'files',
            label: 'Video snippets',
            hint: 'Your raw clips',
            valid: files.length > 0,
            render: () => (
              <div className="space-y-1">
                <Input type="file" accept={VIDEO_ACCEPT} multiple onChange={onPickFiles} className="cursor-pointer" />
                {files.length ? <p className="text-xs text-muted-foreground">{files.length} clip{files.length === 1 ? '' : 's'} selected</p> : null}
              </div>
            ),
          },
          {
            id: 'title',
            label: 'Reel title',
            valid: Boolean(title.trim()),
            render: () => <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Weekend trek highlights" />,
          },
        ]}
      />

      {/* ── STEP: intro ── */}
      <AssistantBubble first>
        <div className="flex items-center gap-2 mb-1">
          <Smartphone className="w-4 h-4 text-rose-300" />
          <span className="font-semibold text-rose-200 text-[12px] uppercase tracking-wider">REEL&SHORTS</span>
        </div>
        <p>Upload your video clips and I'll split, transcribe, and let the AI editor cut them into a polished vertical reel.</p>
      </AssistantBubble>

      {showSetupForm && !projectId ? (
        <>
          {/* ── STEP FILES ── */}
          <AssistantBubble>
            <p className="font-medium">Drop your video snippets below.</p>
            <p className="text-[12px] text-[#9CA3AF] mt-0.5">MP4, MOV, WebM — up to 24 clips.</p>
          </AssistantBubble>

          <ChatInputArea>
            <div className="space-y-2">
              <input
                ref={fileRef}
                type="file"
                accept={VIDEO_ACCEPT}
                multiple
                onChange={onPickFiles}
                className="block w-full text-sm text-slate-400 file:mr-3 file:rounded-md file:border-0 file:bg-rose-500/20 file:px-3 file:py-1.5 file:text-rose-200 cursor-pointer"
              />
              {files.length ? (
                <ul className="text-[12px] text-[#9CA3AF] space-y-0.5">
                  {files.map((f) => <li key={`${f.name}-${f.size}`}>📎 {f.name} · {(f.size / (1024 * 1024)).toFixed(1)} MB</li>)}
                </ul>
              ) : null}
              <Button
                size="sm"
                onClick={advanceToTitle}
                disabled={!files.length}
                className="bg-rose-500 hover:bg-rose-400 text-black text-xs font-semibold"
              >
                Next →
              </Button>
            </div>
          </ChatInputArea>

          {/* ── STEP TITLE ── */}
          <AnimatePresence>
            {[STEP.TITLE, STEP.LANGUAGE, STEP.BROLL, STEP.CONFIRM].includes(chatStep) && (
              <motion.div key="title-step" initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}>
                <UserBubble>{files.length} clip{files.length === 1 ? '' : 's'} selected ✓</UserBubble>
                <AssistantBubble>What's the title for your reel?</AssistantBubble>
                <ChatInputArea>
                  <div className="flex gap-2">
                    <Input
                      value={titleDraft}
                      onChange={(e) => setTitleDraft(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && advanceToLanguage()}
                      placeholder="Summer market reel…"
                      className="bg-[#0d1017] border-white/10 text-sm h-9"
                    />
                    <Button size="sm" onClick={advanceToLanguage} disabled={!titleDraft.trim()} className="bg-rose-500 hover:bg-rose-400 text-black shrink-0">→</Button>
                  </div>
                </ChatInputArea>
              </motion.div>
            )}
          </AnimatePresence>

          {/* ── STEP LANGUAGE ── */}
          <AnimatePresence>
            {[STEP.LANGUAGE, STEP.BROLL, STEP.CONFIRM].includes(chatStep) && (
              <motion.div key="lang-step" initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}>
                <UserBubble>"{title}" ✓</UserBubble>
                <AssistantBubble>
                  <p>What language are the clips in?</p>
                  <p className="text-[11px] text-[#9CA3AF] mt-0.5">Gemini writes speech as spoken — pick Hindi to keep Devanagari, not romanized Hinglish.</p>
                </AssistantBubble>
                <ChatInputArea>
                  <div className="flex gap-2">
                    <Select value={language || 'auto'} onValueChange={setLanguage}>
                      <SelectTrigger className="bg-[#0d1017] border-white/10 text-xs h-9 flex-1">
                        <SelectValue placeholder="Auto (as spoken)" />
                      </SelectTrigger>
                      <SelectContent className="z-[200]">
                        {TRANSCRIPT_LANGUAGES.map((l) => <SelectItem key={l.code} value={l.code}>{l.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <Button size="sm" onClick={advanceToBroll} className="bg-rose-500 hover:bg-rose-400 text-black shrink-0">→</Button>
                  </div>
                </ChatInputArea>
              </motion.div>
            )}
          </AnimatePresence>

          {/* ── STEP BROLL ── */}
          <AnimatePresence>
            {[STEP.BROLL, STEP.CONFIRM].includes(chatStep) && (
              <motion.div key="broll-step" initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}>
                <UserBubble>{languageLabel(language)} ✓</UserBubble>
                <AssistantBubble>
                  <p>Choose your AI director settings.</p>
                  <p className="text-[11px] text-[#9CA3AF] mt-0.5">B-ROLL look, editing mode, and how aggressively to cut dead air.</p>
                </AssistantBubble>
                <ChatInputArea>
                  <div className="space-y-3">
                    <ReelsShortsBrollLookPicker compact value={brollVisualStyle} onChange={setBrollVisualStyle} disabled={loading} testId="reels-shorts-create-broll-look" />
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      <label className="space-y-1">
                        <span className="text-[10px] uppercase tracking-wide text-[#6B7280]">Editing mode</span>
                        <Select value={editingMode} onValueChange={setEditingMode} disabled={loading}>
                          <SelectTrigger className="bg-[#0d1017] border-white/10 text-xs h-9" data-testid="reels-shorts-create-mode">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="z-[200]">
                            <SelectItem value="auto">Auto Edit</SelectItem>
                            <SelectItem value="creator">Creator</SelectItem>
                            <SelectItem value="podcast">Podcast</SelectItem>
                            <SelectItem value="educational">Educational</SelectItem>
                            <SelectItem value="news">News</SelectItem>
                            <SelectItem value="cinematic">Cinematic</SelectItem>
                            <SelectItem value="viral">Viral</SelectItem>
                            <SelectItem value="minimal">Minimal</SelectItem>
                          </SelectContent>
                        </Select>
                      </label>
                      <label className="space-y-1">
                        <span className="text-[10px] uppercase tracking-wide text-[#6B7280]">Cleanup</span>
                        <Select value={cleanupLevel} onValueChange={setCleanupLevel} disabled={loading}>
                          <SelectTrigger className="bg-[#0d1017] border-white/10 text-xs h-9" data-testid="reels-shorts-create-cleanup">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="z-[200]">
                            <SelectItem value="natural">Natural</SelectItem>
                            <SelectItem value="balanced">Balanced</SelectItem>
                            <SelectItem value="aggressive">Aggressive</SelectItem>
                          </SelectContent>
                        </Select>
                      </label>
                    </div>
                    <Button size="sm" onClick={advanceToConfirm} className="bg-rose-500 hover:bg-rose-400 text-black text-xs font-semibold">→</Button>
                  </div>
                </ChatInputArea>
              </motion.div>
            )}
          </AnimatePresence>

          {/* ── STEP CONFIRM / LAUNCH ── */}
          <AnimatePresence>
            {chatStep === STEP.CONFIRM && (
              <motion.div key="confirm-step" initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}>
                <UserBubble>Director settings chosen ✓</UserBubble>
                <AssistantBubble>
                  <p className="font-medium">Ready to process!</p>
                  <div className="mt-2 text-[12px] text-[#9CA3AF] space-y-0.5">
                    <p>📎 {files.length} clip{files.length === 1 ? '' : 's'}</p>
                    <p>📝 "{title}"</p>
                    <p>🌐 {languageLabel(language)}</p>
                    <p>🎬 {editingMode} · cleanup {cleanupLevel}</p>
                  </div>
                </AssistantBubble>
                <ChatInputArea>
                  <Button
                    onClick={createProject}
                    disabled={loading}
                    className="bg-rose-500 hover:bg-rose-400 text-black font-semibold"
                  >
                    {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Upload className="w-4 h-4 mr-2" />}
                    {uploadProgress || 'Upload & process'}
                  </Button>
                </ChatInputArea>
              </motion.div>
            )}
          </AnimatePresence>
        </>
      ) : null}

      {/* ── Project loaded: show chat summary + controls ── */}
      {projectId ? (
        <>
          <ChatSection label="Project loaded" />
          <AssistantBubble>
            <p className="font-medium">"{project?.title || 'Untitled'}"</p>
            <p className="text-[12px] text-[#9CA3AF] mt-0.5">
              {(project?.snippets || []).length} clip{(project?.snippets || []).length === 1 ? '' : 's'} · {languageLabel(project?.language || language)}
            </p>
          </AssistantBubble>

          {/* pipeline progress in chat */}
          {jobRunning ? (
            <AssistantBubble>
              <div className="space-y-1.5">
                <div className="flex items-center justify-between text-[11px]">
                  <span className="font-semibold text-rose-200">
                    {isRenderProgress ? `${progress}% video rendered` : `${progress}% complete`}
                  </span>
                  {displayJob?.clips_total ? (
                    <span className="text-[#6B7280]">{displayJob.clips_done || 0}/{displayJob.clips_total} clips</span>
                  ) : null}
                </div>
                <Progress value={progress} className="h-1.5" />
                <p className="text-[11px] text-[#6B7280]">{displayJob?.message || 'Working…'}</p>
              </div>
            </AssistantBubble>
          ) : null}

          {/* action chips */}
          {(project?.snippets || []).length ? (
            <ChatInputArea>
              <div className="flex flex-wrap gap-2">
                <Button
                  type="button" size="sm"
                  onClick={runEditor}
                  disabled={!canRunEditor || cutting || languageBusy || rendering || renderRunning || recutting}
                  className="bg-rose-500 hover:bg-rose-400 text-black text-xs"
                >
                  {cutting && !recutting ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <TwinneeLogo className="w-3.5 h-3.5 mr-1" />}
                  AI editor cut
                </Button>
                <Button
                  type="button" size="sm" variant="secondary"
                  onClick={recutPolishedReel}
                  disabled={!canRunEditor || cutting || languageBusy || rendering || renderRunning || recutting}
                  className="text-xs"
                  title="Re-run AI editor on the same clips and export again. Old MP4s stay in history."
                >
                  {recutting ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <History className="w-3.5 h-3.5 mr-1" />}
                  Re-cut polished reel
                </Button>
                <Button type="button" size="sm" variant="secondary" onClick={cleanAllAudio} disabled={cleaningAll || languageBusy} className="text-xs">
                  {cleaningAll ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <AudioLines className="w-3.5 h-3.5 mr-1" />}
                  Clean audio
                </Button>
                <Button type="button" size="sm" variant="secondary"
                  onClick={() => changeTranscriptLanguage(language || 'auto', { retranscribe: true })}
                  disabled={languageBusy || cleaningAll} className="text-xs"
                >
                  {languageBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : null}
                  Re-transcribe
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="secondary"
                  onClick={retry}
                  disabled={loading || jobRunning || languageBusy || cleaningAll || cutting || recutting}
                  className="text-xs"
                  title="Re-run Split A/V + transcribe on the same uploads. Rebuilds mixed + per-speaker audio tracks."
                  data-testid="reels-shorts-retry-split-av"
                >
                  {loading || (pipelineRunning && !isRenderProgress) ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" />
                  ) : (
                    <RotateCcw className="w-3.5 h-3.5 mr-1" />
                  )}
                  Retry Split A/V
                </Button>
              </div>
            </ChatInputArea>
          ) : null}

          <ChatInputArea>
            <button
              type="button"
              onClick={resetForNewProject}
              className="text-[11px] text-[#6B7280] hover:text-white flex items-center gap-1 transition-colors cursor-pointer"
            >
              <Plus className="w-3 h-3" /> New project
            </button>
          </ChatInputArea>
        </>
      ) : null}

      <div ref={chatEndRef} />
    </>
  );

  /* ── Right panel: pipeline status + outputs ──────────────────────────── */
  const rightContent = projectId ? (
    <div className="px-4 sm:px-6 py-5 space-y-6">
      {/* pipeline stepper */}
      <div>
        <RightPanelHeading>Pipeline</RightPanelHeading>
        <ReelsShortsPipeline activeStep={activeStep} job={displayJob} complete={Boolean(project?.final_video_url)} />
      </div>

      {/* Screen Composition Planner phases A–E (live during export) */}
      {(renderRunning || rendering || project?.final_video_url || renderJob?.composition_stage) ? (
        <div>
          <RightPanelHeading>
            <span className="flex items-center gap-1.5">Composition phases</span>
          </RightPanelHeading>
          <ReelsShortsCompositionStages
            job={renderJob || (isRenderProgress ? displayJob : null)}
            active={renderRunning || rendering}
            complete={Boolean(project?.final_video_url) && !renderRunning}
          />
        </div>
      ) : null}

      {/* transcripts */}
      {(project?.snippets || []).length ? (
        <div>
          <RightPanelHeading>Mapped transcriptions</RightPanelHeading>
          <div className="flex items-center gap-2 flex-wrap mb-3">
            <Select value={language || 'auto'} onValueChange={(code) => changeTranscriptLanguage(code)} disabled={languageBusy}>
              <SelectTrigger className="h-8 w-[160px] bg-[#12151d] border-[#2a3142] text-xs">
                <SelectValue placeholder="Auto (as spoken)" />
              </SelectTrigger>
              <SelectContent className="z-[200]">
                {TRANSCRIPT_LANGUAGES.map((l) => <SelectItem key={l.code} value={l.code}>{l.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <ReelsShortsTranscriptPanel
            projectId={projectId}
            snippets={project.snippets}
            language={language}
            disabled={changingLanguage || cleaningAll}
            onUpdated={(next) => setProject(next)}
          />
        </div>
      ) : null}

      {/* editor cut */}
      <div>
        <RightPanelHeading>
          <span className="flex items-center gap-1.5"><Film className="w-3.5 h-3.5" /> Editor timeline</span>
        </RightPanelHeading>
        <ReelsShortsEditorCutPanel project={project} onUpdated={(next) => setProject(next)} />
      </div>

      <div>
        <RightPanelHeading>
          <span className="flex items-center gap-1.5"><Smartphone className="w-3.5 h-3.5" /> Multi-format canvas</span>
        </RightPanelHeading>
        <ReelsShortsLayoutEngine
          projectId={projectId}
          formats={project?.edit_plan?.formats}
          screenCompositions={project?.edit_plan?.screen_compositions}
          snippets={project?.snippets || []}
          disabled={languageBusy || cutting}
          generatingAll={rendering || renderRunning}
          onGenerateAllFormats={(selectedFormats) => void renderFinal({
            generateAllFormats: true,
            formats: selectedFormats,
          })}
          onUpdated={(next) => {
            if (next) setProject(next);
          }}
        />
      </div>

      {/* final video */}
      <ReelsShortsFinalVideoPanel
        project={project}
        includeBroll={includeBroll}
        onIncludeBrollChange={setIncludeBroll}
        includeSubtitles={includeSubtitles}
        onIncludeSubtitlesChange={setIncludeSubtitles}
        onRender={() => void renderFinal()}
        rendering={rendering}
        disabled={languageBusy || !(project?.snippets || []).length}
        renderJob={renderJob}
        onOpenEditor={() => navigate(reelsShortsEditorPath(projectId))}
        onRecut={() => void recutPolishedReel()}
        recutting={recutting}
        onRestoreVersion={(row) => void restoreRenderVersion(row)}
        restoringVersionId={restoringVersionId}
        onRetryRender={async () => {
          if (!renderJob?.id) { await renderFinal(); return; }
          setRendering(true);
          try {
            await api.post(`/jobs/${renderJob.id}/restart`);
            setRenderJob({ ...renderJob, status: 'pending', progress: 0, error_message: null, message: 'Retry queued' });
            toast.info('Retry queued');
          } catch (err) { toast.error(formatApiError(err, 'Retry failed')); }
          finally { setRendering(false); }
        }}
      />
    </div>
  ) : (
    /* pre-project: show catalog */
    <div className="px-4 sm:px-6 py-5 space-y-6">
      <RightPanelHeading>B-ROLL catalog</RightPanelHeading>
      <p className="text-[12px] text-[#6B7280]">The AI editor only suggests these layouts — managed as a closed catalog.</p>
      <ReelsShortsBrollCatalog />
      <RightPanelHeading>Hook title templates</RightPanelHeading>
      <p className="text-[12px] text-[#6B7280]">Stacked Instagram-style bars at the top of the reel.</p>
      <ReelsShortsTitleCatalog />
    </div>
  );

  return (
    <>
      <ReelsShortsHistoryPanel
        open={historyOpen}
        onToggle={toggleHistory}
        activeProjectId={queryProjectId || projectId}
        onSelectProject={selectHistoryProject}
        onNewProject={resetForNewProject}
        refreshKey={historyRefreshKey}
        loadingProjectId={loadingHistoryId}
      />

      <FlowChatLayout
        flowLabel="REEL&SHORTS"
        flowColor="#f43f5e"
        onBack={() => navigate('/dashboard')}
        headerRight={
          <button
            type="button"
            onClick={toggleHistory}
            className="h-8 px-3 rounded-full border border-white/10 text-[11px] text-[#9CA3AF] hover:text-white hover:bg-white/5 flex items-center gap-1.5 cursor-pointer"
          >
            <Clapperboard className="w-3.5 h-3.5" />
            History
          </button>
        }
        leftContent={leftContent}
        rightContent={rightContent}
        showRight
      />
    </>
  );
}

export default ReelsShortsFlowPage;
